import type { Customer } from '@/api/customersApi'
import type { PoDraft, PoDraftShipTo } from '@/lib/purchaseOrderDraft'
import type { PoTemplateLayout } from '@/types/purchaseOrders'

/**
 * Who a purchase order is to and where it goes (z8nrz7f7dz), picked from what
 * the tenant already has: vendors from Partners (category Vendor or Supplier),
 * ship-to addresses from the PO template's default and any partner with an
 * address. Pure, so the mapping is tested; the pickers only call these.
 */

export const VENDOR_CATEGORIES = ['Vendor', 'Supplier'] as const

type VendorFields = Pick<
  PoDraft,
  'supplierName' | 'supplierContact' | 'supplierAddress' | 'supplierPhone' | 'supplierEmail'
>

const s = (v: string | null | undefined): string => (v ?? '').trim()

/** The street part of a partner's address: the structured lines, else the flat column. */
function street(c: Customer): string {
  const a = c.postalAddress
  const lines = a ? [a.addressLine1, a.addressLine2, a.addressLine3].map(s).filter(Boolean) : []
  return lines.length > 0 ? lines.join(', ') : s(c.address)
}

function city(c: Customer): string {
  return s(c.postalAddress?.city) || s(c.city)
}

function postcode(c: Customer): string {
  return s(c.postalAddress?.postalCode) || s(c.zipCode)
}

function country(c: Customer): string {
  return s(c.postalAddress?.countryCode) || s(c.country)
}

/**
 * A partner's address on one line, as a supplier block prints it:
 * "Rue de jumet, BE-6010 Jumet". Country is prefixed to the postcode the way
 * European addresses write it; blank when the partner has no address.
 */
export function partnerAddressLine(c: Customer): string {
  const cc = country(c)
  const pc = postcode(c)
  const place = [pc ? (cc ? `${cc}-${pc}` : pc) : cc, city(c)].filter(Boolean).join(' ')
  return [street(c), place].filter(Boolean).join(', ')
}

/** Whether a partner has anything to ship to. */
export function partnerHasAddress(c: Customer): boolean {
  return street(c) !== '' || city(c) !== ''
}

/** The vendor block of a draft, filled from a partner. Every field stays editable after. */
export function vendorFromPartner(c: Customer): VendorFields {
  return {
    supplierName: s(c.name),
    supplierContact: s(c.contactPerson),
    supplierAddress: partnerAddressLine(c),
    supplierPhone: s(c.phone),
    supplierEmail: s(c.email),
  }
}

/** A ship-to filled from a partner: its attention-to as the contact when it has one. */
export function shipToFromPartner(c: Customer): PoDraftShipTo {
  return {
    name: s(c.postalAddress?.companyName) || s(c.name),
    street: street(c),
    postcode: postcode(c),
    city: city(c),
    country: country(c),
    phone: s(c.phone),
    email: s(c.email),
  }
}

/** The template's default ship-to as draft fields; null when the template has none. */
export function shipToFromTemplate(layout: Pick<PoTemplateLayout, 'shipTo'> | null | undefined): PoDraftShipTo | null {
  const t = layout?.shipTo
  if (!t || !(s(t.name) || s(t.street) || s(t.city))) return null
  return {
    name: s(t.name),
    street: s(t.street),
    postcode: s(t.postcode),
    city: s(t.city),
    country: s(t.country),
    phone: s(t.phone),
    email: s(t.email),
  }
}

/** The partner a draft's vendor name already names, matched without case or edge spaces. */
export function matchPartnerByName<T extends Pick<Customer, 'name'>>(partners: readonly T[], name: string): T | null {
  const n = name.trim().toLowerCase()
  if (!n) return null
  return partners.find((p) => p.name.trim().toLowerCase() === n) ?? null
}

/** Whether two ship-to blocks hold the same address, so a picker can show which one is in use. */
export function sameShipTo(a: PoDraftShipTo, b: PoDraftShipTo): boolean {
  const keys: (keyof PoDraftShipTo)[] = ['name', 'street', 'postcode', 'city', 'country']
  return keys.every((k) => s(a[k]).toLowerCase() === s(b[k]).toLowerCase())
}
